/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Ref } from 'preact';
import { forwardRef } from 'preact/compat';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';

type IntrinsicProps = Pick<
  HTMLAttributesSignalExcluded<HTMLDivElement>,
  'id' | 'class' | 'children'
>;

type TrackProps = IntrinsicProps & {
  length?: string;
  color?: string;
  orientation?: 'vertical' | 'horizontal';
  ariaProps?: {
    'aria-valuenow'?: HTMLAttributesSignalExcluded['aria-valuenow'];
    'aria-valuemin'?: HTMLAttributesSignalExcluded['aria-valuemin'];
    'aria-valuemax'?: HTMLAttributesSignalExcluded['aria-valuemax'];
    'aria-valuetext'?: HTMLAttributesSignalExcluded['aria-valuetext'];
    'aria-label'?: HTMLAttributesSignalExcluded['aria-label'];
    role?: HTMLAttributesSignalExcluded['role'];
  };
};

export const BarTrack = forwardRef(
  (
    {
      id,
      length = '100%',
      children,
      color,
      orientation = 'horizontal',
      ariaProps,
      ...otherProps
    }: TrackProps,
    ref?: Ref<HTMLDivElement>
  ) => {
    const isHoriz = orientation === 'horizontal';
    return (
      <div
        id={id}
        ref={ref}
        class={otherProps.class}
        {...ariaProps}
        style={{
          width: isHoriz ? length : undefined,
          height: isHoriz ? undefined : length,
          backgroundColor: color
        }}>
        {children}
      </div>
    );
  }
);

type ValueProps = Pick<IntrinsicProps, 'class'> & {
  length?: string;
  size?: string;
  orientation?: 'vertical' | 'horizontal';
  color?: string;
};

export const BarValue = ({
  length,
  size = '100%',
  orientation = 'horizontal',
  color,
  ...otherProps
}: ValueProps) => {
  const isHoriz = orientation === 'horizontal';
  return (
    <div
      class={otherProps.class}
      style={{
        width: isHoriz ? length : size,
        height: isHoriz ? size : length,
        backgroundColor: color
      }}></div>
  );
};
